export const SHOP_LIST_PAGE_SIZES = [15, 20, 25, 50, 100] as const;
export const SHOP_LIST_PAGE_SIZE = 15;

export type ShopListPageSize = (typeof SHOP_LIST_PAGE_SIZES)[number];

export type PaginatedList = {
  list: any[];
  total: number;
  page: number;
  limit: number;
};

export type ShopListRequest = {
  lang: string;
  page: number;
  limit: number;
  vehicleId?: number;
  categoryId?: number;
  sort?: string;
};

/** UI sort key -> API `sort` value (price_asc|price_desc|name_asc|best_sellers). */
const LIST_SORTS: Record<string, string> = {
  priceLowToHigh: "price_asc",
  priceHighToLow: "price_desc",
  nameAZ: "name_asc",
  topRated: "best_sellers",
};

export function resolveListSort(sort: string | null | undefined): string | undefined {
  return sort ? LIST_SORTS[sort] : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function positiveInt(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

function nonNegativeInt(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : fallback;
}

/** Normalize Odoo list payloads: `{ total, page, limit, items }` or a raw array. */
export function extractPaginatedList(body: unknown): PaginatedList {
  if (Array.isArray(body)) {
    return {
      list: body,
      total: body.length,
      page: 1,
      limit: body.length || SHOP_LIST_PAGE_SIZE,
    };
  }

  const root = asRecord(body) ?? {};
  const nested = asRecord(root.result) ?? root;
  const list = (
    [
      nested.items,
      nested.data,
      nested.result,
      root.items,
      root.data,
    ].find(Array.isArray) as any[] | undefined
  ) ?? [];

  return {
    list,
    total: nonNegativeInt(nested.total ?? root.total, list.length),
    page: positiveInt(nested.page ?? root.page, 1),
    limit: positiveInt(nested.limit ?? root.limit, list.length || SHOP_LIST_PAGE_SIZE),
  };
}

export function resolveListPage(page: number | undefined): number {
  return positiveInt(page, 1);
}

export function resolveListLimit(limit: number | undefined): number {
  const n = positiveInt(limit, SHOP_LIST_PAGE_SIZE);
  return SHOP_LIST_PAGE_SIZES.includes(n as ShopListPageSize)
    ? n
    : SHOP_LIST_PAGE_SIZE;
}

export function resolveCategoryId(
  categoryId: string | number | null | undefined
): number | undefined {
  if (categoryId == null || categoryId === "") return undefined;
  const parsed = Number(categoryId);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export function isSameShopListRequest(
  current: ShopListRequest | undefined,
  next: ShopListRequest
): boolean {
  if (!current) return false;
  return (
    current.lang === next.lang &&
    current.page === next.page &&
    current.limit === next.limit &&
    current.vehicleId === next.vehicleId &&
    current.categoryId === next.categoryId &&
    current.sort === next.sort
  );
}
